import React, { useEffect, useState } from 'react';
import './Cart.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMultiCart } from '../../Context/MultiCartContext';
import RestaurantCart from '../../components/RestaurantCart/RestaurantCart';

// Utility function to ensure consistent string keys for React
const getStringKey = (id, fallback = '') => {
    if (!id) return fallback;
    if (typeof id === 'string') return id;
    
    // Handle populated MongoDB documents
    if (typeof id === 'object' && id._id) {
        return id._id.toString();
    }
    
    if (id.toString && typeof id.toString === 'function') return id.toString();
    return String(id) || fallback;
};

const Cart = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const {
        restaurantCarts,
        savedCarts,
        sharedCarts,
        loading,
        error,
        fetchUserCarts,
        clearAllCarts,
        getTotalItemCount,
        saveCart,
        shareCart,
        loadSharedCart,
        setError
    } = useMultiCart();

    const [shareModalCart, setShareModalCart] = useState(null);
    const [shareUrl, setShareUrl] = useState('');

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Load carts on component mount
    useEffect(() => {
        // Force refresh cart data to ensure we have the latest state
        console.log('Cart page: Force refreshing cart data...');
        fetchUserCarts();
    }, []); // Only run once on mount

    const handleCheckout = (cart) => {
        // Navigate to the new two-step checkout process
        navigate('/order-details', { state: { restaurantCart: cart } });
    };

    const handleAddMoreItems = (restaurantId) => {
        navigate(`/restaurant/${restaurantId}`);
    };

    const continueShopping = () => {
        navigate('/');
    };

    const handleClearAllCarts = async () => {
        if (window.confirm(t('cart.confirm_clear_all'))) {
            await clearAllCarts();
        }
    };

    const handleShareCart = async (cart) => {
        try {
            // Save the cart with public sharing enabled to get a share ID
            const restaurantId = getStringKey(cart.restaurantId);
            const cartName = t('cart.share_cart_name', { restaurantName: cart.restaurantName });
            const description = t('cart.share_cart_description', { restaurantName: cart.restaurantName, itemCount: cart.items?.length || 0 });
            
            const saveResult = await saveCart(restaurantId, cartName, description, true);
            
            if (saveResult && saveResult.shareId) {
                // Create the proper share URL pointing to our SharedCart component
                const shareUrl = `${window.location.origin}/#/cart/shared/${saveResult.shareId}`;
                setShareModalCart(cart);
                setShareUrl(shareUrl);
            } else {
                // Fallback to simple restaurant sharing if cart sharing fails
                const fallbackUrl = `${window.location.origin}/#/restaurant/${restaurantId}`;
                setShareModalCart(cart);
                setShareUrl(fallbackUrl);
                setError(t('cart.share_fallback_error'));
            }
        } catch (err) {
            console.error('Error creating shareable cart:', err);
            // Fallback to simple restaurant sharing
            const restaurantId = getStringKey(cart.restaurantId);
            const fallbackUrl = `${window.location.origin}/#/restaurant/${restaurantId}`;
            setShareModalCart(cart);
            setShareUrl(fallbackUrl);
            setError(t('cart.share_failed_error'));
        }
    };

    const copyShareUrl = () => {
        navigator.clipboard.writeText(shareUrl);
        alert(t('cart.share_url_copied'));
    };

    if (loading) {
        return (
            <div className="cart-loading">
                <div className="loading-spinner"></div>
                <p>{t('cart.loading')}</p>
            </div>
        );
    }

    return (
        <div className="cart-page">
            <div className="cart-header">
                <h1>{t('cart.title')}</h1>
                <div className="header-controls">
                    <button className="continue-shopping-btn" onClick={continueShopping}>
                        {t('cart.continue_shopping')}
                    </button>
                </div>
            </div>

            {error && (
                <div className="error-message">
                    <span>{error}</span>
                    <button onClick={() => setError(null)}>×</button>
                </div>
            )}

            {restaurantCarts.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">🛒</div>
                    <h2>{t('cart.empty_title')}</h2>
                    <p>{t('cart.empty_description')}</p>
                    <button className="start-shopping-btn" onClick={continueShopping}>
                        {t('cart.start_shopping')}
                    </button>
                </div>
            ) : (
                <div className="restaurant-carts-container">
                    {restaurantCarts.map((cart, index) => {
                        const cartKey = getStringKey(cart.restaurantId, `cart-${index}`);
                        
                        return (
                            <RestaurantCart 
                                key={cartKey} 
                                cart={cart}
                                onCheckout={handleCheckout}
                                onAddMoreItems={handleAddMoreItems}
                                onShareCart={handleShareCart}
                                simple={true}
                            />
                        );
                    })}
                </div>
            )}

            {/* Share Modal */}
            {shareModalCart && (
                <div className="modal-overlay">
                    <div className="share-modal">
                        <h3>{t('cart.share_modal_title', { restaurantName: shareModalCart.restaurantName })}</h3>
                        <p>{t('cart.share_modal_description')}</p>
                        <div className="share-url-container">
                            <input 
                                type="text" 
                                value={shareUrl} 
                                readOnly 
                                className="share-url-input"
                            />
                            <button onClick={copyShareUrl} className="copy-btn">
                                {t('cart.copy_link')}
                            </button>
                        </div>
                        <div className="modal-actions">
                            <button 
                                className="close-btn"
                                onClick={() => setShareModalCart(null)}
                            >
                                {t('cart.close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Cart;
